import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Grid3x3, ChevronRight } from 'lucide-react';
import { useRecentMoodboards } from '@/hooks/use-moodboards';
import { useAddToMoodboard } from '@/hooks/use-moodboard-items';
import { CreateMoodboardDialog } from './CreateMoodboardDialog';
import { MoodboardDrawer } from './MoodboardDrawer';
import type { SaveToMoodboardPayload } from '@/types/moodboards';

interface SaveToPieMenuProps {
  isOpen: boolean;
  onClose: () => void;
  item: SaveToMoodboardPayload;
  position: { x: number; y: number };
}

export function SaveToPieMenu({ isOpen, onClose, item, position }: SaveToPieMenuProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const { data: recentMoodboards = [] } = useRecentMoodboards(5);
  const addToMoodboard = useAddToMoodboard();

  // Smart positioning - only adjust if menu would go off-screen
  const getAdjustedPosition = () => {
    const menuRadius = 100; // Account for button radius + item radius
    const padding = 10;
    
    let x = position.x;
    let y = position.y;
    
    // Only adjust if it would go off-screen
    const maxX = window.innerWidth - menuRadius - padding;
    const maxY = window.innerHeight - menuRadius - padding;
    const minX = menuRadius + padding;
    const minY = menuRadius + padding;
    
    // Only constrain if actually needed
    if (x > maxX) x = maxX;
    if (x < minX) x = minX;
    if (y > maxY) y = maxY;
    if (y < minY) y = minY;
    
    return { x, y };
  };

  const adjustedPosition = getAdjustedPosition();

  const handleAddToMoodboard = async (moodboardId: string) => {
    await addToMoodboard.mutateAsync({ moodboardId, item });
    onClose();
  };

  const handleCreateNew = () => {
    setShowCreateDialog(true);
  };

  const handleShowAll = () => {
    // Open the main navigation menu drawer instead
    window.dispatchEvent(new Event('nav:open-menu'));
    onClose();
  };

  // Calculate positions for radial menu
  const radius = 80;
  const itemCount = Math.min(recentMoodboards.length, 4) + 2; // recent + "new" + "see all"
  const angleStep = (Math.PI * 1.5) / (itemCount - 1); // 270 degrees arc
  const startAngle = -Math.PI * 0.75; // Start from top-left

  const getItemPosition = (index: number) => {
    const angle = startAngle + angleStep * index;
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  };

  const menuItems = [
    { type: 'new', icon: Plus, label: 'New', action: handleCreateNew, id: 'new' },
    ...recentMoodboards.slice(0, 4).map((mb, idx) => ({
      type: 'moodboard',
      icon: mb.icon,
      label: mb.name,
      action: () => handleAddToMoodboard(mb.id),
      id: mb.id,
    })),
    { type: 'all', icon: ChevronRight, label: 'See All', action: handleShowAll, id: 'all' },
  ];

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] bg-black/20"
              onClick={onClose}
            />

            {/* Radial Menu */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="fixed z-[10000]"
              style={{
                left: adjustedPosition.x,
                top: adjustedPosition.y,
                transform: 'translate(-50%, -50%)',
              }}
            >
              {/* Center Button */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                style={{ width: 48, height: 48, left: -24, top: -24 }}
              >
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg">
                  <Grid3x3 className="w-5 h-5 text-primary-foreground" />
                </div>
              </motion.div>

              {/* Menu Items */}
              {menuItems.map((menuItem, index) => {
                const pos = getItemPosition(index);
                const Icon = typeof menuItem.icon === 'string' ? null : menuItem.icon;
                const iconDisplay = typeof menuItem.icon === 'string' ? menuItem.icon : null;

                return (
                  <motion.button
                    key={menuItem.id}
                    initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      x: pos.x,
                      y: pos.y,
                    }}
                    exit={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                    transition={{
                      type: 'spring',
                      damping: 15,
                      stiffness: 300,
                      delay: index * 0.03,
                    }}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      menuItem.action();
                    }}
                    className="absolute w-14 h-14 rounded-full bg-white dark:bg-gray-800 shadow-lg flex flex-col items-center justify-center gap-0.5 group hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    style={{
                      left: -28,
                      top: -28,
                    }}
                  >
                    {Icon ? (
                      <Icon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    ) : (
                      <span className="text-2xl">{iconDisplay}</span>
                    )}
                    <span className="text-[9px] font-medium text-gray-600 dark:text-gray-400 truncate max-w-[48px] px-1">
                      {menuItem.label}
                    </span>
                  </motion.button>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Create Moodboard Dialog */}
      <CreateMoodboardDialog
        isOpen={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          onClose();
        }}
        initialItem={item}
      />

      {/* All Moodboards Drawer */}
      <MoodboardDrawer
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          onClose();
        }}
        item={item}
        onSelect={handleAddToMoodboard}
      />
    </>
  );
}
